import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Interfaces para la respuesta de ExifTool
export interface ExifItem {
  label: string;
  value: string;
}

export interface ExifToolResult {
  metadata: ExifItem[];
  hasManipulationSigns: boolean;
  manipulationIndicators: string[];
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BrowserExiftoolService {
  // URL para la descarga del archivo WASM
  private wasmUrl = 'https://sam2-model-download.b-cdn.net/zeroperl.wasm'; 
  private wasmModule: any = null;
  private isInitialized = false;
  private isInitializing = false;

  // Lista de propiedades que indican posible manipulación
  private suspiciousProperties = [
    'software', 'photoshop', 'edited', 'modified', 'creator', 'ai', 'neural',
    'lightroom', 'gimp', 'illustrator', 'edit', 'history', 'modification',
  ];

  constructor(private http: HttpClient) {}

  /**
   * Inicializa el servicio de ExifTool
   */
  public async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    if (this.isInitializing) {
      // Esperar a que termine la inicialización en curso
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.isInitialized) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 100);
      });
    }

    this.isInitializing = true;

    try {
      // Intentar cargar el módulo WASM
      console.log('Inicializando ExifTool...');
      
      // En una implementación completa, aquí se cargaría el módulo WASM
      // Sin embargo, esto requiere una configuración específica de Webpack

      this.isInitialized = true;
      this.isInitializing = false;
      return true;
    } catch (error) {
      console.error('Error al inicializar ExifTool:', error);
      this.isInitializing = false;
      return false;
    }
  }

  /**
   * Extrae los metadatos de una imagen
   */
  public async extractMetadata(file: File): Promise<ExifToolResult> {
    try {
      // Intenta inicializar ExifTool
      const initialized = await this.initialize();
      if (!initialized) {
        throw new Error('No se pudo inicializar ExifTool');
      }

      // En una implementación completa, aquí se ejecutaría ExifTool sobre el archivo
      // Pero como alternativa, vamos a usar la API de ExifTool-Web

      // Extraer metadatos básicos que podemos obtener directamente
      const basicMetadata = await this.extractBasicMetadata(file);
      
      // Analizar los metadatos para detectar manipulaciones
      const manipulationAnalysis = this.analyzeForManipulation(basicMetadata);

      return {
        metadata: basicMetadata,
        hasManipulationSigns: manipulationAnalysis.hasManipulationSigns,
        manipulationIndicators: manipulationAnalysis.indicators,
      };
    } catch (error) {
      console.error('Error al extraer metadatos con ExifTool:', error);
      return {
        metadata: [],
        hasManipulationSigns: false,
        manipulationIndicators: [],
        error: 'Error al procesar el archivo con ExifTool',
      };
    }
  }

  /**
   * Extrae metadatos básicos usando la API de File y exifReader
   */
  private async extractBasicMetadata(file: File): Promise<ExifItem[]> {
    try {
      // Usar exifReader (que ya está disponible) para obtener datos básicos
      const exifReader = require('exifreader');
      const tags = await exifReader.load(file);

      const metadata: ExifItem[] = [];

      // Convertir los datos de exifReader a nuestro formato
      for (const [key, value] of Object.entries(tags)) {
        if (value && typeof value === 'object' && 'description' in value) {
          metadata.push({
            label: key,
            value: String(value.description || ''),
          });
        }
      }

      // Añadir información del archivo
      metadata.push({ label: 'FileName', value: file.name });
      metadata.push({ label: 'FileSize', value: this.formatFileSize(file.size) });
      metadata.push({ label: 'FileType', value: file.type });
      metadata.push({ label: 'LastModified', value: new Date(file.lastModified).toLocaleString() });

      return metadata;
    } catch (error) {
      console.error('Error al extraer metadatos básicos:', error);
      return [];
    }
  }

  /**
   * Formatea el tamaño de archivo en KB/MB
   */
  private formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return bytes + ' bytes';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }

  /**
   * Analiza metadatos para detectar posibles manipulaciones
   */
  private analyzeForManipulation(metadata: ExifItem[]): { hasManipulationSigns: boolean; indicators: string[] } {
    const indicators: string[] = [];

    // Buscar propiedades sospechosas
    for (const item of metadata) {
      const label = item.label.toLowerCase();
      const value = item.value.toLowerCase();

      // Verificar si la propiedad o valor contienen palabras sospechosas
      const isSuspiciousLabel = this.suspiciousProperties.some(prop => label.includes(prop));
      const isSuspiciousValue = this.suspiciousProperties.some(prop => value.includes(prop));

      if (isSuspiciousLabel || isSuspiciousValue) {
        indicators.push(`Propiedad sospechosa: ${item.label}: ${item.value}`);
      }
    }

    // Verificar software usado
    const softwareItem = metadata.find(item => item.label === 'Software');
    if (softwareItem) {
      const softwareLower = softwareItem.value.toLowerCase();
      const editingSoftware = ['photoshop', 'lightroom', 'gimp', 'illustrator', 'affinity', 'capture'];
      
      if (editingSoftware.some(s => softwareLower.includes(s))) {
        indicators.push(`Software de edición detectado: ${softwareItem.value}`);
      }
    }

    // Verificar fechas
    const creationDate = metadata.find(item => 
      item.label === 'CreateDate' || item.label === 'DateTimeOriginal');
    const modifyDate = metadata.find(item => 
      item.label === 'ModifyDate' || item.label === 'LastModified');

    if (creationDate && modifyDate) {
      try {
        const createTime = new Date(creationDate.value).getTime();
        const modifyTime = new Date(modifyDate.value).getTime();
        
        if (!isNaN(createTime) && !isNaN(modifyTime)) {
          const diffMinutes = Math.abs(modifyTime - createTime) / (1000 * 60);
          
          if (diffMinutes > 5) {
            indicators.push(`Tiempo entre creación y modificación: ${Math.round(diffMinutes)} minutos`);
          }
        }
      } catch (e) {
        // Error al procesar fechas, ignorar
      }
    }

    // Verificar datos EXIF incompletos
    const hasMake = metadata.some(item => item.label === 'Make');
    const hasModel = metadata.some(item => item.label === 'Model');

    if (!hasMake || !hasModel) {
      indicators.push('Falta información del dispositivo (Make/Model)');
    }

    return {
      hasManipulationSigns: indicators.length > 0,
      indicators,
    };
  }

  /**
   * Descarga un archivo WASM
   */
  private async downloadWasmModule(): Promise<ArrayBuffer> {
    const response = await fetch(this.wasmUrl);
    if (!response.ok) {
      throw new Error(`Error al descargar el módulo WASM: ${response.status}`);
    }
    return response.arrayBuffer();
  }
}
